import { isDictationCustomVocabularyEnabled } from '@/features/flags';
import {
  type CustomVocabularyEntryInput,
  type CustomVocabularyResponse,
  DICTATION_CUSTOM_VOCABULARY_KEY,
} from '@heidi/dictate/contracts';
import { getCustomVocabulary, getDictationDataConfig } from '@heidi/dictate/data';

/**
 * The app cache crosses through the dictation data seam (its `getQueryClient`
 * member — the one way the app cache crosses; the app wiring binds the
 * singleton). LOCAL FAIL-LOUD rather than a silent `undefined`: a missing
 * getter would hand a module-scope cache read no cache and no error, which is
 * the trap the seam member's own doc names.
 */
const getQueryClient = () => {
  const client = getDictationDataConfig().getQueryClient?.();
  if (!client) {
    throw new Error(
      '[dictationBubble/store] query client not wired — configureDictationData must supply getQueryClient'
    );
  }
  return client;
};

// Total — empty list on ANY error so a vocabulary outage can never block a
// dictation (same contract as fetchUserSnippets).
const fetchCustomVocabulary = async (): Promise<CustomVocabularyResponse> => {
  try {
    return await getCustomVocabulary();
  } catch {
    return { entries: [] };
  }
};

/**
 * Each term with its misspellings; the entry `id` stays on the client.
 * Counts are bounded where the clinician types: entries by
 * `MAX_CUSTOM_VOCABULARY_ENTRIES`, misspellings by
 * `MAX_CUSTOM_VOCABULARY_MISSPELLINGS_PER_ENTRY` in the dialog. No truncation
 * here — a silent drop after a confirmed save is invisible to the user.
 *
 * `misspellings` is defaulted because `getCustomVocabulary` is a bare cast, not
 * runtime-validated: an absent field would otherwise reach the request body as
 * `undefined`. Both callers read this above their `try`, so a throw here fails
 * the dictation rather than degrading it. Mirrors `#[serde(default)]` on the
 * Rust `CustomVocabularyEntry`.
 */
export const getCurrentCustomVocabularyEntries = (): CustomVocabularyEntryInput[] =>
  (
    getQueryClient().getQueryData<CustomVocabularyResponse>([
      DICTATION_CUSTOM_VOCABULARY_KEY,
    ])?.entries ?? []
  ).map(({ term, misspellings }) => ({ term, misspellings: misspellings ?? [] }));

// Awaitable cache-or-fetch so the vocabulary is loaded before the synchronous
// read in buildUserContext. No-op when the flag is off: users outside the
// rollout never fetch it.
export const ensureCurrentCustomVocabulary = async (): Promise<void> => {
  if (!isDictationCustomVocabularyEnabled()) return;
  await getQueryClient().ensureQueryData<CustomVocabularyResponse>({
    queryKey: [DICTATION_CUSTOM_VOCABULARY_KEY],
    queryFn: fetchCustomVocabulary,
  });
};
